import { styled } from '@grupoboticario/flora-react-system'

export const StyledButton = styled('button', {
  display: 'inline-flex',
  pointerEvents: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$2',
  appearance: 'none',
  userSelect: 'none',
  outline: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  border: '1px solid transparent',
  borderRadius: '$small',
  transitionDuration: '300ms',
  transitionProperty: 'background-color, border-color, color',
  transitionTimingFunction: 'ease-out',

  '&:disabled, &[aria-disabled="true"]': {
    '&, &:hover, &:focus, &:active': {
      pointerEvents: 'none',
      cursor: 'auto',
    },
  },

  '&:focus-visible': {
    outlineStyle: 'solid',
    outlineWidth: '2px',
    outlineOffset: '2px',
  },

  variants: {
    /**
     * Opcional, especifica a hierarquia do botão de acordo com o tipo de estilização escolhida.
     */
    hierarchy: {
      primary: {
        '&:disabled, &[aria-disabled="true"]': {
          '&, &:hover, &:focus, &:active': {
            backgroundColor: '$disabledBackground',
            color: '$disabledContent',
          },
        },
      },
      secondary: {
        backgroundColor: 'transparent',

        '&:disabled, &[aria-disabled="true"]': {
          '&, &:hover, &:focus, &:active': {
            border: '1px solid $disabledBackground',
            backgroundColor: 'transparent',
            color: '$disabledContent',
          },
        },
      },
      tertiary: {
        backgroundColor: 'transparent',

        '&:disabled, &[aria-disabled="true"]': {
          '&, &:hover, &:focus, &:active': {
            backgroundColor: 'transparent',
            color: '$disabledContent',
          },
        },
      },
    },

    /**
     * Opcional, especifica o conjunto de cores do botão.
     */
    styleSemantic: {
      neutral: {},
      conversion: {},
      warning: {},
      success: {},
      alert: {},
    },

    /**
     * Opcional, adapta cores do botão para aplicações em superfícies light ou dark.
     */
    surfaceColor: {
      light: {
        '&:focus-visible': {
          outlineColor: '$colors$keyboardFocusDefault',
        },
      },
      dark: {
        '&:focus-visible': {
          outlineColor: '$colors$keyboardFocusAlt',
        },
      },
    },

    /**
     * Opcional, especifica o tamanho do botão.
     */
    size: {
      small: {
        fontSize: '$bodySmall',
        padding: 'calc($2 - 1px)',
        lineHeight: '1.1428571429',
        height: '32px',
      },
      medium: {
        fontSize: '$bodySmall',
        padding: 'calc($4 - 1px)',
        lineHeight: '1.1428571429',
        height: '48px',
      },
      large: {
        fontSize: '$bodyLarge',
        padding: 'calc(1.375rem - 1px) calc($5 - 1px)',
        lineHeight: '$shorter',
        height: '64px',
      },
    },

    /**
     * Opcional, especifica se o botão ocupará toda a largura do container.
     */
    isFull: {
      true: {
        width: '100%',
      },
    },

    /**
     * Opcional, especifica se o texto do botão será em caixa alta.
     */
    isUppercase: {
      true: {
        textTransform: 'uppercase',
      },
    },

    /**
     * Opcional, especifica o posicionamento do ícone.
     */
    has: {
      iconRight: {
        flexDirection: 'row',
      },
      iconLeft: {
        flexDirection: 'row-reverse',
      },
      iconOnly: {},
    },

    hasIcon: {
      true: {},
    },

    hasIconOnly: {
      true: {},
    },

    /**
     * Opcional, especifica se o loading está ativado ou não.
     */
    isLoading: {
      true: {
        pointerEvents: 'none',
      },
    },
  },

  compoundVariants: [
    /**
     * Hierarchy: Primary
     */
    {
      hierarchy: 'primary',
      styleSemantic: 'neutral',
      css: {
        backgroundColor: '$actionableDefault',
        color: '$actionableContent',

        '&:hover': {
          backgroundColor: '$actionableHover',
        },

        '&:active': {
          backgroundColor: '$actionableActive',
        },
      },
    },
    {
      hierarchy: 'primary',
      styleSemantic: 'warning',
      css: {
        backgroundColor: '$statusErrorActionableDefault',
        color: '$statusErrorActionableContent',

        '&:hover': {
          backgroundColor: '$statusErrorActionableHover',
        },

        '&:active': {
          backgroundColor: '$statusErrorActionableActive',
        },
      },
    },
    {
      hierarchy: 'primary',
      styleSemantic: 'success',
      css: {
        backgroundColor: '$statusSuccessActionableDefault',
        color: '$statusSuccessActionableContent',

        '&:hover': {
          backgroundColor: '$statusSuccessActionableHover',
        },

        '&:active': {
          backgroundColor: '$statusSuccessActionableActive',
        },
      },
    },
    {
      hierarchy: 'primary',
      styleSemantic: 'alert',
      css: {
        backgroundColor: '$statusAlertActionableDefault',
        color: '$statusAlertActionableContent',

        '&:hover': {
          backgroundColor: '$statusAlertActionableHover',
        },

        '&:active': {
          backgroundColor: '$statusAlertActionableActive',
        },
      },
    },
    {
      hierarchy: 'primary',
      styleSemantic: 'neutral',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$actionableAlt',
        color: '$actionableAltContent',

        '&:hover': {
          backgroundColor: '$actionableAltHover',
        },

        '&:active': {
          backgroundColor: '$actionableAltActive',
        },
      },
    },
    {
      hierarchy: 'primary',
      styleSemantic: 'warning',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$statusErrorActionableAlt',
        color: '$statusErrorActionableAltContent',

        '&:hover': {
          backgroundColor: '$statusErrorActionableAltHover',
        },

        '&:active': {
          backgroundColor: '$statusErrorActionableAltActive',
        },
      },
    },
    {
      hierarchy: 'primary',
      styleSemantic: 'success',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$statusSuccessActionableAlt',
        color: '$statusSuccessActionableAltContent',

        '&:hover': {
          backgroundColor: '$statusSuccessActionableAltHover',
        },

        '&:active': {
          backgroundColor: '$statusSuccessActionableAltActive',
        },
      },
    },
    {
      hierarchy: 'primary',
      styleSemantic: 'alert',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$statusAlertActionableAlt',
        color: '$statusAlertActionableAltContent',

        '&:hover': {
          backgroundColor: '$statusAlertActionableAltHover',
        },

        '&:active': {
          backgroundColor: '$statusAlertActionableAltActive',
        },
      },
    },
    {
      hierarchy: 'primary',
      surfaceColor: 'dark',
      css: {
        '&:disabled, &[aria-disabled="true"]': {
          '&, &:hover, &:focus, &:active': {
            backgroundColor: '$disabledAltBackground',
            color: '$disabledAltContent',
          },
        },
      },
    },

    /**
     * Hierarchy: Secondary
     */
    {
      hierarchy: 'secondary',
      styleSemantic: 'neutral',
      css: {
        backgroundColor: '$nonPrimaryButtonDefault',
        borderColor: '$nonPrimaryButtonStroke',
        color: '$nonPrimaryButtonContent',

        '&:hover': {
          backgroundColor: '$nonPrimaryButtonHover',
        },

        '&:active': {
          backgroundColor: '$nonPrimaryButtonActive',
        },
      },
    },
    {
      hierarchy: 'secondary',
      styleSemantic: 'warning',
      css: {
        backgroundColor: '$statusErrorNonPrimaryButtonDefault',
        borderColor: '$statusErrorNonPrimaryButtonStroke',
        color: '$statusErrorNonPrimaryButtonContent',

        '&:hover': {
          backgroundColor: '$statusErrorNonPrimaryButtonHover',
        },

        '&:active': {
          backgroundColor: '$statusErrorNonPrimaryButtonActive',
        },
      },
    },
    {
      hierarchy: 'secondary',
      styleSemantic: 'success',
      css: {
        backgroundColor: '$statusSuccessNonPrimaryButtonDefault',
        borderColor: '$statusSuccessNonPrimaryButtonStroke',
        color: '$statusSuccessNonPrimaryButtonContent',

        '&:hover': {
          backgroundColor: '$statusSuccessNonPrimaryButtonHover',
        },

        '&:active': {
          backgroundColor: '$statusSuccessNonPrimaryButtonActive',
        },
      },
    },
    {
      hierarchy: 'secondary',
      styleSemantic: 'alert',
      css: {
        backgroundColor: '$statusAlertNonPrimaryButtonDefault',
        borderColor: '$statusAlertNonPrimaryButtonStroke',
        color: '$statusAlertNonPrimaryButtonContent',

        '&:hover': {
          backgroundColor: '$statusAlertNonPrimaryButtonHover',
        },

        '&:active': {
          backgroundColor: '$statusAlertNonPrimaryButtonActive',
        },
      },
    },
    {
      hierarchy: 'secondary',
      styleSemantic: 'neutral',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$nonPrimaryButtonAlt',
        borderColor: '$nonPrimaryButtonAltStroke',
        color: '$nonPrimaryButtonAltContent',

        '&:hover': {
          backgroundColor: '$nonPrimaryButtonAltHover',
        },

        '&:active': {
          backgroundColor: '$nonPrimaryButtonAltActive',
        },
      },
    },
    {
      hierarchy: 'secondary',
      styleSemantic: 'warning',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$statusErrorNonPrimaryButtonAlt',
        borderColor: '$statusErrorNonPrimaryButtonAltStroke',
        color: '$statusErrorNonPrimaryButtonAltContent',

        '&:hover': {
          backgroundColor: '$statusErrorNonPrimaryButtonAltHover',
        },

        '&:active': {
          backgroundColor: '$statusErrorNonPrimaryButtonAltActive',
        },
      },
    },
    {
      hierarchy: 'secondary',
      styleSemantic: 'success',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$statusSuccessNonPrimaryButtonAlt',
        borderColor: '$statusSuccessNonPrimaryButtonAltStroke',
        color: '$statusSuccessNonPrimaryButtonAltContent',

        '&:hover': {
          backgroundColor: '$statusSuccessNonPrimaryButtonAltHover',
        },

        '&:active': {
          backgroundColor: '$statusSuccessNonPrimaryButtonAltActive',
        },
      },
    },
    {
      hierarchy: 'secondary',
      styleSemantic: 'alert',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$statusAlertNonPrimaryButtonAlt',
        borderColor: '$statusAlertNonPrimaryButtonAltStroke',
        color: '$statusAlertNonPrimaryButtonAltContent',

        '&:hover': {
          backgroundColor: '$statusAlertNonPrimaryButtonAltHover',
        },

        '&:active': {
          backgroundColor: '$statusAlertNonPrimaryButtonAltActive',
        },
      },
    },
    {
      hierarchy: 'secondary',
      surfaceColor: 'dark',
      css: {
        '&:disabled, &[aria-disabled="true"]': {
          '&, &:hover, &:focus, &:active': {
            border: '1px solid $disabledAltBackground',
            backgroundColor: 'transparent',
            color: '$disabledAltContent',
          },
        },
      },
    },

    /**
     * Hierarchy: Tertiary
     */
    {
      hierarchy: 'tertiary',
      styleSemantic: 'neutral',
      css: {
        backgroundColor: '$nonPrimaryButtonDefault',
        color: '$nonPrimaryButtonContent',

        '&:hover': {
          backgroundColor: '$nonPrimaryButtonHover',
        },

        '&:active': {
          backgroundColor: '$nonPrimaryButtonActive',
        },
      },
    },
    {
      hierarchy: 'tertiary',
      styleSemantic: 'warning',
      css: {
        backgroundColor: '$statusErrorNonPrimaryButtonDefault',
        color: '$statusErrorNonPrimaryButtonContent',

        '&:hover': {
          backgroundColor: '$statusErrorNonPrimaryButtonHover',
        },

        '&:active': {
          backgroundColor: '$statusErrorNonPrimaryButtonActive',
        },
      },
    },
    {
      hierarchy: 'tertiary',
      styleSemantic: 'success',
      css: {
        backgroundColor: '$statusSuccessNonPrimaryButtonDefault',
        color: '$statusSuccessNonPrimaryButtonContent',

        '&:hover': {
          backgroundColor: '$statusSuccessNonPrimaryButtonHover',
        },

        '&:active': {
          backgroundColor: '$statusSuccessNonPrimaryButtonActive',
        },
      },
    },
    {
      hierarchy: 'tertiary',
      styleSemantic: 'alert',
      css: {
        backgroundColor: '$statusAlertNonPrimaryButtonDefault',
        color: '$statusAlertNonPrimaryButtonContent',

        '&:hover': {
          backgroundColor: '$statusAlertNonPrimaryButtonHover',
        },

        '&:active': {
          backgroundColor: '$statusAlertNonPrimaryButtonActive',
        },
      },
    },
    {
      hierarchy: 'tertiary',
      styleSemantic: 'neutral',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$nonPrimaryButtonAlt',
        color: '$nonPrimaryButtonAltContent',

        '&:hover': {
          backgroundColor: '$nonPrimaryButtonAltHover',
        },

        '&:active': {
          backgroundColor: '$nonPrimaryButtonAltActive',
        },
      },
    },
    {
      hierarchy: 'tertiary',
      styleSemantic: 'warning',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$statusErrorNonPrimaryButtonAlt',
        color: '$statusErrorNonPrimaryButtonAltContent',

        '&:hover': {
          backgroundColor: '$statusErrorNonPrimaryButtonAltHover',
        },

        '&:active': {
          backgroundColor: '$statusErrorNonPrimaryButtonAltActive',
        },
      },
    },
    {
      hierarchy: 'tertiary',
      styleSemantic: 'success',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$statusSuccessNonPrimaryButtonAlt',
        color: '$statusSuccessNonPrimaryButtonAltContent',

        '&:hover': {
          backgroundColor: '$statusSuccessNonPrimaryButtonAltHover',
        },

        '&:active': {
          backgroundColor: '$statusSuccessNonPrimaryButtonAltActive',
        },
      },
    },
    {
      hierarchy: 'tertiary',
      styleSemantic: 'alert',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$statusAlertNonPrimaryButtonAlt',
        color: '$statusAlertNonPrimaryButtonAltContent',

        '&:hover': {
          backgroundColor: '$statusAlertNonPrimaryButtonAltHover',
        },

        '&:active': {
          backgroundColor: '$statusAlertNonPrimaryButtonAltActive',
        },
      },
    },
    {
      hierarchy: 'tertiary',
      surfaceColor: 'dark',
      css: {
        '&:disabled, &[aria-disabled="true"]': {
          '&, &:hover, &:focus, &:active': {
            backgroundColor: 'transparent',
            color: '$disabledAltContent',
          },
        },
      },
    },

    /**
     * Semantic: Conversion
     */
    {
      styleSemantic: 'conversion',
      css: {
        backgroundColor: '$conversionButtonDefault',
        color: '$conversionButtonContent',

        '&:hover': {
          backgroundColor: '$conversionButtonHover',
        },

        '&:active': {
          backgroundColor: '$conversionButtonActive',
        },
      },
    },
    {
      styleSemantic: 'conversion',
      surfaceColor: 'dark',
      css: {
        backgroundColor: '$conversionButtonAlt',
        color: '$conversionButtonAltContent',

        '&:hover': {
          backgroundColor: '$conversionButtonAltHover',
        },

        '&:active': {
          backgroundColor: '$conversionButtonAltActive',
        },
      },
    },

    /**
     * Size: Large
     */
    {
      size: 'large',
      hasIconOnly: true,
      css: {
        padding: 'calc($6 - 1px)',
      },
    },

    /**
     * isLoading
     */
    {
      isLoading: true,
      hasIcon: false,
      css: {
        '.button-label': {
          opacity: 0,
        },

        '.button-icon': {
          position: 'absolute',
        },
      },
    },
  ],

  defaultVariants: {
    styleSemantic: 'neutral',
    size: 'medium',
    hierarchy: 'primary',
    surfaceColor: 'light',
  },
})

export const ButtonIcon = styled('span', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  svg: {
    width: '$4',
    height: '$4',
  },
})
